import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { currentHousehold } from '@/lib/currentHousehold'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useMyHouseholds } from '@/hooks/useMyHouseholds'

interface CurrentHouseholdContextValue {
  householdId: string | null
  /** The caller's role in the *active* household — not necessarily their role in whichever
   *  household /users/me reports, since the user may have switched away from that one. Sourced
   *  from /households/mine (per-household, unlike /users/me's single embedded role), null while
   *  that hasn't loaded yet. */
  role: string | null
  /** Whether the active household itself is archived (read-only for every role, owner included —
   *  distinct from VIEWER, which is a per-member role). Sourced from /households/mine, same as
   *  role. */
  isArchived: boolean
  /** Whether the active household is the seeded sample — billing, invites and Start fresh are hidden, and a watermark shows. */
  isSample: boolean
  /** Switches the active household and resets every cached query so the app refetches under the
   *  new X-Household-Id — cheaper than hunting down every household-scoped query key by hand. */
  switchHousehold: (id: string) => void
}

const CurrentHouseholdContext = createContext<CurrentHouseholdContextValue | null>(null)

export function CurrentHouseholdProvider({ children }: { children: ReactNode }) {
  const [explicitId, setExplicitId] = useState<string | null>(currentHousehold.get)
  const queryClient = useQueryClient()
  const { data: user } = useCurrentUser()
  const { data: households } = useMyHouseholds()

  // A stored id can outlive the account it belonged to — e.g. a stale localStorage value left
  // over from a previous Clerk user in the same browser (sign-out not going through
  // signOutAndReset, or a session simply expiring) — sending it as X-Household-Id would 403 with
  // NOT_A_HOUSEHOLD_MEMBER for the now-signed-in user. Once /households/mine has actually loaded,
  // treat an id that isn't in that list as if nothing were stored.
  const explicitIdIsStale = households !== undefined && !households.some((h) => h.id === explicitId)
  const effectiveExplicitId = explicitId !== null && !explicitIdIsStale ? explicitId : null

  // Nothing stored yet (onboarding case, not a multi-household one) — default to whatever
  // /users/me reports and persist it, without overriding an explicit switch.
  const householdId = effectiveExplicitId ?? user?.household?.id ?? null

  const activeHousehold = households?.find((h) => h.id === householdId)
  const role = activeHousehold?.role ?? null
  const isArchived = !!activeHousehold?.archivedAt
  const isSample = !!activeHousehold?.isSample

  useEffect(() => {
    // Only the localStorage side effect here, not setExplicitId — effectiveExplicitId already
    // recomputes to null on every render while this is stale, so state doesn't need to track it.
    if (explicitId !== null && explicitIdIsStale) {
      currentHousehold.clear()
    }
  }, [explicitId, explicitIdIsStale])

  useEffect(() => {
    if (effectiveExplicitId === null && user?.household) {
      currentHousehold.set(user.household.id)
    }
  }, [effectiveExplicitId, user])

  const switchHousehold = useCallback(
    (id: string) => {
      currentHousehold.set(id)
      setExplicitId(id)
      // Not clear(): it empties the cache without telling mounted queries, so whatever is on
      // screen keeps showing the old household until it remounts. reset refetches them.
      void queryClient.resetQueries()
    },
    [queryClient]
  )

  return (
    <CurrentHouseholdContext.Provider
      value={{ householdId, role, isArchived, isSample, switchHousehold }}
    >
      {children}
    </CurrentHouseholdContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentHousehold(): CurrentHouseholdContextValue {
  const ctx = useContext(CurrentHouseholdContext)
  if (!ctx) throw new Error('useCurrentHousehold must be used within a CurrentHouseholdProvider')
  return ctx
}

/** True once the active household's role is known to be VIEWER — false (not just "unknown")
 *  while it's still loading, so gated UI defaults to hidden rather than briefly flashing visible. */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsViewer(): boolean {
  const { role } = useCurrentHousehold()
  return role === 'VIEWER'
}

/** True once the active household is known to be archived — read-only for every role, owner
 *  included (server-enforced in HouseholdGateFilter), unlike VIEWER which is per-member. */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsArchivedHousehold(): boolean {
  const { isArchived } = useCurrentHousehold()
  return isArchived
}
